import React, { useMemo } from "react";
import { TableRow, TableCell, IconButton, TextField } from "@mui/material";
import { FastField } from "formik";
import DeleteIcon from "../../Images/delete.png";
import { Dropdown } from "../../HelperFunctions/PayrollDropdown";
import LocalTextField from "../shared/LocalTextField";
import { ITEM_TYPE } from "../../Enums/Enum";
import {
    ADD_DED_OPTIONS,
    TAX_OPTIONS,
    MAPPED_TO_BASE,
    TABLE_STYLES,
    renderMappedToOption,
    renderCoaOption,
    formatAmountBlur,
} from "./addDeductTaxUtils";

/**
 * Shared row component for both Additions/Deductions and Taxes tables.
 * Eliminates duplicate JSX and logic between AddDedRow and TaxRow.
 *
 * Exactly 3 grouped props:
 *   rowData  — { row, index, isRemoving, touchedRow, errorRow }
 *   ctx      — { isTax, showItemColumn, isReadOnly, formValuesDepartment, formValuesJob,
 *                mappedToOptionsMap, mappedToLookup, accountOptionsMap, accountOptions }
 *   handlers — { setFieldValue, enqueueSnackbar, fetchCoaDetails, onAddNewMappedItem,
 *                remove, setRemovingIndex, showDeleteConfirm, setOpenAddCOA, setPendingCOAField }
 */
const AddDeductTaxRow = React.memo(function AddDeductTaxRow(props) {
    // Gracefully handle either 3-prop grouped pattern or legacy flat props
    const rowData = props.rowData || {
        row: props.row,
        index: props.index,
        isRemoving: props.isRemoving,
        touchedRow: props.touchedRow,
        errorRow: props.errorRow,
    };
    const ctx = props.ctx || {
        isTax: props.isTax ?? (props.row?.taxType !== undefined),
        showItemColumn: props.showItemColumn,
        isReadOnly: props.isReadOnly,
        formValuesDepartment: props.formValuesDepartment,
        formValuesJob: props.formValuesJob,
        mappedToOptionsMap: props.mappedToOptionsMap,
        mappedToLookup: props.mappedToLookup,
        accountOptionsMap: props.accountOptionsMap,
        accountOptions: props.accountOptions,
    };
    const handlers = props.handlers || {
        setFieldValue: props.setFieldValue,
        enqueueSnackbar: props.enqueueSnackbar,
        fetchCoaDetails: props.fetchCoaDetails,
        onAddNewMappedItem: props.onAddNewMappedItem,
        remove: props.remove,
        setRemovingIndex: props.setRemovingIndex || props.setRemovingTaxIndex,
        showDeleteConfirm: props.showDeleteConfirm,
        setOpenAddCOA: props.setOpenAddCOA,
        setPendingCOAField: props.setPendingCOAField,
    };

    const { row, index, isRemoving, touchedRow, errorRow } = rowData;
    const {
        isTax,
        showItemColumn,
        isReadOnly,
        formValuesDepartment,
        mappedToOptionsMap,
        mappedToLookup,
        accountOptionsMap,
        accountOptions,
    } = ctx;
    const {
        setFieldValue,
        enqueueSnackbar,
        fetchCoaDetails,
        onAddNewMappedItem,
        remove,
        setRemovingIndex,
        showDeleteConfirm,
        setOpenAddCOA,
        setPendingCOAField,
    } = handlers;

    // Configuration depending on whether this is an Add/Ded row or a Tax row
    const fieldName = isTax ? "eeAndErTaxes" : "addAndDeductions";
    const typeKey = isTax ? "taxType" : "addDeduction";
    const typeOptions = isTax ? TAX_OPTIONS : ADD_DED_OPTIONS;
    const typeLabel = isTax ? "EE & ER Taxes" : "Additions/Deduction";

    const currentTypeValue = Number(row[typeKey]) || row[typeKey];
    const selectedType = useMemo(() =>
        typeOptions.find((o) => Number(o.value) === Number(currentTypeValue)) || typeOptions[0],
        [typeOptions, currentTypeValue]
    );

    const currentMappedOptions = mappedToOptionsMap?.[currentTypeValue] || mappedToOptionsMap?.[row[typeKey]] || MAPPED_TO_BASE;
    const typeMap = mappedToLookup?.[currentTypeValue] || mappedToLookup?.[row[typeKey]];
    const selectedMappedTo = useMemo(() =>
        (typeMap && (typeMap.get(String(row.mappedTo)) || typeMap.get(String(row.mappedTo).toLowerCase()))) ||
        { ItemID: row.mappedTo || "", PayrollItemName: row.mappedTo ? (row.mappedToName || row.mappedTo || "") : "--Select--" },
        [typeMap, row.mappedTo, row.mappedToName]
    );

    const rowMappedOptions = useMemo(() => {
        const opts = currentMappedOptions || [];
        if (selectedMappedTo?.ItemID && !opts.some((o) => String(o.ItemID || o.ItemId || o.value || "").toLowerCase() === String(selectedMappedTo.ItemID).toLowerCase())) {
            return [...opts, selectedMappedTo];
        }
        return opts;
    }, [currentMappedOptions, selectedMappedTo]);

    const selectedCOAOption = useMemo(() => {
        const coaKey = String(row.coa || "");
        return coaKey
            ? (accountOptionsMap?.get(coaKey) || accountOptionsMap?.get(coaKey.toLowerCase()) || accountOptionsMap?.get(coaKey.toUpperCase()) || { label: row.coaName || coaKey, value: row.coa })
            : (accountOptionsMap?.get("") || { label: "--Select--", value: "" });
    }, [row.coa, row.coaName, accountOptionsMap]);

    const rowAccountOptions = useMemo(() => {
        const opts = accountOptions || [];
        if (selectedCOAOption?.value && !accountOptionsMap?.has(String(selectedCOAOption.value).toLowerCase())) {
            return [...opts, selectedCOAOption];
        }
        return opts;
    }, [accountOptions, selectedCOAOption, accountOptionsMap]);

    return (
        <TableRow sx={{ backgroundColor: "#ffffff", opacity: isRemoving ? 0 : 1, transform: isRemoving ? "translateY(-5px)" : "translateY(0)", transition: "all 0.2s ease" }}>
            {/* Category / Type Column (Additions/Deductions OR EE & ER Taxes) */}
            <TableCell sx={{ height: "35px", boxSizing: "border-box", p: "5px" }}>
                <Dropdown
                    name={`${fieldName}[${index}].${typeKey}`}
                    disabled={isReadOnly}
                    options={typeOptions}
                    error={touchedRow?.[typeKey] && Boolean(errorRow?.[typeKey])}
                    getOptionLabel={(opt) => opt.label}
                    isEqual={(opt, val) => Number(opt.value) === Number(val?.value)}
                    value={selectedType}
                    onChange={(name, newValue) => {
                        const val = newValue?.value || "";
                        setFieldValue(`${fieldName}[${index}]`, {
                            ...row,
                            [typeKey]: val,
                            mappedTo: "",
                            mappedToName: "",
                            coa: "",
                            coaName: "",
                            ...(isTax ? { expenseCoa: "" } : {}),
                        });
                    }}
                    sx={TABLE_STYLES.dropdown}
                />
            </TableCell>

            {/* Item Column (import only) */}
            {showItemColumn && (
                <TableCell sx={{ height: "35px", boxSizing: "border-box", p: "5px" }}>
                    <FastField name={`${fieldName}[${index}].item`}>
                        {({ field, meta }) => (
                            <TextField
                                disabled
                                {...field}
                                size="small"
                                fullWidth
                                placeholder=""
                                error={meta.touched && Boolean(meta.error)}
                                sx={{ ...TABLE_STYLES.input, "& .MuiOutlinedInput-input": { padding: "4px 8px", textAlign: "left" } }}
                                title={field.value}
                            />
                        )}
                    </FastField>
                </TableCell>
            )}

            {/* Mapped To Column */}
            <TableCell sx={{ height: "35px", boxSizing: "border-box", p: "5px" }}>
                <Dropdown
                    name={`${fieldName}[${index}].mappedTo`}
                    disabled={isReadOnly}
                    options={rowMappedOptions}
                    error={touchedRow?.mappedTo && Boolean(errorRow?.mappedTo)}
                    getOptionLabel={(opt) => opt.PayrollItemName || opt.ItemName || ""}
                    isEqual={(opt, val) => String(opt.ItemID || opt.ItemId || opt.Id || opt.value || "") === String(val?.ItemID || val?.ItemId || val?.Id || val?.value || "")}
                    value={selectedMappedTo}
                    renderOption={renderMappedToOption}
                    onChange={async (name, newValue) => {
                        if (newValue?.ItemID === "ADD_NEW") {
                            if (!row[typeKey]) {
                                enqueueSnackbar(`Please select ${typeLabel}`, { variant: "error" });
                                return;
                            }
                            if (!formValuesDepartment) {
                                enqueueSnackbar("Please select the Department ", { variant: "error" });
                                return;
                            }
                            if (onAddNewMappedItem) onAddNewMappedItem(row[typeKey], row.item || "", index, fieldName);
                            return;
                        }

                        const selectedVal = newValue?.ItemID || "";
                        const account = selectedVal ? await fetchCoaDetails(selectedVal) : null;

                        if (isTax) {
                            const isErTax = Number(row.taxType) === ITEM_TYPE.EmployerTaxes || newValue?.ItemType === ITEM_TYPE.EmployerTaxes;
                            const fetchedCoa = account?.AccountID || account?.COA || newValue?.AccountID || newValue?.COA || "";
                            const fetchedCoaName = account?.AccountName || account?.COAName || newValue?.AccountName || newValue?.COAName || "";
                            const fetchedExpenseCoa =
                                account?.LiabilityAccountID ||
                                account?.ExpenseAccountID ||
                                account?.ExpenseCOA ||
                                account?.PayableCOA ||
                                newValue?.LiabilityAccountID ||
                                newValue?.ExpenseAccountID ||
                                newValue?.ExpenseCOA ||
                                newValue?.PayableCOA ||
                                (isErTax ? fetchedCoa : "");

                            setFieldValue(`${fieldName}[${index}]`, {
                                ...row,
                                mappedTo: selectedVal,
                                mappedToName: newValue?.PayrollItemName || newValue?.ItemName || "",
                                coa: fetchedCoa,
                                coaName: fetchedCoa ? fetchedCoaName : "",
                                expenseCoa: isErTax ? (fetchedExpenseCoa || fetchedCoa || "") : "",
                            });
                        } else {
                            const fetchedCoa = account?.AccountID || "";
                            setFieldValue(`${fieldName}[${index}]`, {
                                ...row,
                                mappedTo: selectedVal,
                                mappedToName: newValue?.PayrollItemName || newValue?.ItemName || "",
                                coa: fetchedCoa,
                                coaName: fetchedCoa ? (account?.AccountName || "") : "",
                            });
                        }
                    }}
                    sx={TABLE_STYLES.dropdown}
                />
            </TableCell>

            {/* COA Column */}
            <TableCell sx={{ height: "35px", boxSizing: "border-box", p: "5px" }}>
                <Dropdown
                    name={`${fieldName}[${index}].coa`}
                    disabled={isReadOnly}
                    options={rowAccountOptions}
                    value={selectedCOAOption}
                    error={touchedRow?.coa && Boolean(errorRow?.coa)}
                    getOptionLabel={(option) => option?.label || ""}
                    isEqual={(option, val) => String(option?.value || option?.ID || "").toLowerCase() === String(val?.value || val?.ID || "").toLowerCase()}
                    onChange={(name, newValue) => {
                        if (newValue?.value === "ADD_NEW") {
                            if (setPendingCOAField && setOpenAddCOA) {
                                setPendingCOAField(`${fieldName}[${index}].coa`);
                                setOpenAddCOA(true);
                            }
                            return;
                        }

                        const selectedCoa = newValue?.value || "";
                        if (isTax) {
                            const isErTax = Number(row.taxType) === ITEM_TYPE.EmployerTaxes;
                            setFieldValue(`${fieldName}[${index}]`, {
                                ...row,
                                coa: selectedCoa,
                                coaName: newValue?.label || "",
                                ...(isErTax && !row.expenseCoa && { expenseCoa: selectedCoa }),
                            });
                        } else {
                            setFieldValue(`${fieldName}[${index}]`, {
                                ...row,
                                coa: selectedCoa,
                                coaName: newValue?.label || "",
                            });
                        }
                    }}
                    renderOption={renderCoaOption}
                    sx={{
                        ...TABLE_STYLES.dropdown,
                        minWidth: 120,
                        "& .MuiFilledInput-root": { ...TABLE_STYLES.dropdown["& .MuiFilledInput-root"], padding: "0 25px 0 0 !important" },
                        "& .MuiFilledInput-input": { padding: "0 5px !important" },
                    }}
                    popperSx={TABLE_STYLES.coaPopper}
                />
            </TableCell>

            {/* Amount Column */}
            <TableCell sx={{ height: "35px", boxSizing: "border-box", p: "5px" }}>
                <LocalTextField
                    size="small"
                    fullWidth
                    name={`${fieldName}[${index}].amount`}
                    value={row.amount ?? ""}
                    error={touchedRow?.amount && Boolean(errorRow?.amount)}
                    disabled={isReadOnly}
                    onValueChange={(val) => {
                        if (/^-?[0-9,]*\.?[0-9]{0,2}$/.test(val)) return true;
                        if (val !== "" && val !== "-") enqueueSnackbar("Only numbers with up to 2 decimal places are allowed.", { variant: "error" });
                        return false;
                    }}
                    onValueBlur={(value) => setFieldValue(`${fieldName}[${index}].amount`, formatAmountBlur(value))}
                    clearOnFocusZero={true}
                    sx={TABLE_STYLES.input}
                />
            </TableCell>

            {/* Delete Column */}
            <TableCell align="center" sx={{ height: "35px", boxSizing: "border-box", p: "5px" }}>
                <IconButton
                    disabled={isReadOnly}
                    size="small"
                    onClick={() => {
                        const hasData = Boolean(row.amount && Number(row.amount) !== 0);
                        const performDelete = () => {
                            if (setRemovingIndex) setRemovingIndex(index);
                            setTimeout(() => {
                                remove(index);
                                if (setRemovingIndex) setRemovingIndex(null);
                            }, 200);
                        };
                        if (hasData) {
                            showDeleteConfirm("Are you sure you want to delete the row?", performDelete);
                        } else {
                            performDelete();
                        }
                    }}
                >
                    <img src={DeleteIcon} alt="Delete" height={18} width={18} />
                </IconButton>
            </TableCell>
        </TableRow>
    );
}, (prev, next) => {
    return (
        prev.rowData === next.rowData &&
        prev.ctx === next.ctx &&
        prev.handlers === next.handlers &&
        prev.row === next.row &&
        prev.index === next.index &&
        prev.isRemoving === next.isRemoving
    );
});

export default AddDeductTaxRow;
